import i18n from 'i18next';
import {Box, HStack, VStack} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {useMemo, useState} from 'react';
import Cropper, {Area} from 'react-easy-crop';

import {isNotNil} from 'ramda';

import {Nullish, useBoolean} from 'shared/web';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {Button} from '../../Button/Button';
import {Label} from '../../Label/Label';
import {showNotification} from '../../Notification/utils/events';
import {Separator} from '../../Separator/Separator';
import {Slider} from '../../Slider/Slider';
import {getCroppedImage} from '../utils/getCroppedImage';

const DEFAULT_CONFIG = {
  maxZoom: 4,
  minZoom: 0.5,
  aspectRatio: 1,
};

interface ImageCropperProps {
  aspectRatio: `${number}/${number}` | Nullish;
  resolution: {width: number; height: number} | Nullish;
  file: File;
  onSubmit: (file: File) => Promise<void>;
  onCloseDialog: () => void;
}

export function ImageCropper(props: ImageCropperProps) {
  const theme = useComponentsTheme();
  const defaultFillColor = theme.colors.palettes.neutral['40']['100'];

  const [crop, setCrop] = useState({x: 0, y: 0});
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [fillColor, setFillColor] = useState(defaultFillColor);
  const [isSubmitting, startSubmitting, stopSubmitting] = useBoolean();

  const image = useMemo(() => URL.createObjectURL(props.file), [props.file]);

  const aspectRatio = useMemo(() => {
    if (!props.aspectRatio) {
      return 1;
    }
    const [width, height] = props.aspectRatio.split('/').map(Number);

    return width / height;
  }, [props.aspectRatio]);

  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getImage = async () => {
    if (!croppedAreaPixels) {
      return props.file;
    }

    return await getCroppedImage(image, croppedAreaPixels, rotation, props.file.name, fillColor);
  };

  const handleSubmit = async (skipCrop?: boolean) => {
    startSubmitting();

    const croppedImage = skipCrop ? props.file : await getImage();

    if (!croppedImage) {
      showNotification.error();
      stopSubmitting();
      return;
    }

    await props.onSubmit(croppedImage);
    props.onCloseDialog();
    stopSubmitting();
  };

  return (
    <VStack>
      <div className={cn(imageCropperVariants())} style={{backgroundColor: fillColor}}>
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          onCropComplete={onCropComplete}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          rotation={rotation}
          maxZoom={DEFAULT_CONFIG.maxZoom}
          minZoom={DEFAULT_CONFIG.minZoom}
          aspect={aspectRatio}
          cropSize={
            isNotNil(props.resolution)
              ? {width: props.resolution?.width, height: props.resolution?.height}
              : undefined
          }
          restrictPosition={false}
        />
      </div>

      <Box padding={4}>
        <HStack width="100%" justify="space-between" spacing={8}>
          <Box flexGrow={1}>
            <Slider
              label={i18n.t('general.labels.zoom')}
              min={DEFAULT_CONFIG.minZoom}
              max={DEFAULT_CONFIG.maxZoom}
              step={0.1}
              formatValue={(value) => `${value.toFixed(1)}x`}
              value={zoom}
              onChange={setZoom}
              displayValue="auto"
            />
          </Box>

          <VStack>
            <Label>{i18n.t('general.labels.fillColor')}</Label>
            <input type="color" value={fillColor} onChange={(e) => setFillColor(e.target.value)} />
          </VStack>

          <Box flexGrow={1}>
            <Slider
              label={i18n.t('general.labels.rotation')}
              min={-180}
              max={180}
              step={1}
              formatValue={(value) => `${value}°`}
              displayValue="auto"
              value={rotation}
              onChange={setRotation}
            />
          </Box>
        </HStack>
        <Separator />

        <HStack justify="flex-end" spacing={2}>
          <Button
            variant="dangerGhost"
            title={i18n.t('general.actions.cancel')}
            onClick={props.onCloseDialog}
            isLoading={isSubmitting}
          />
          <Button
            variant="secondary"
            title={i18n.t('general.actions.skipCrop')}
            onClick={() => handleSubmit(true)}
            isLoading={isSubmitting}
          />
          <Button
            variant="primary"
            title={i18n.t('general.actions.crop')}
            onClick={() => handleSubmit(false)}
            isLoading={isSubmitting}
          />
        </HStack>
      </Box>
    </VStack>
  );
}

const imageCropperVariants = cva('relative h-[calc(var(--eag-space-step,0.25)*150rem)] w-full');
